import { useFormContext } from 'react-hook-form';
import { CharitableGivingOptions } from '../../../api/useGoalsQuery';
import { CharitableGoalDataDTO } from '../CharitableGiving.meta';
import {
  Donor,
  GivingStrategy,
} from '../../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useCharitableGivingQuery';

interface Params {
  options: CharitableGivingOptions;
}

export const useFormUtils = ({ options }: Params) => {
  const { watch } = useFormContext<CharitableGoalDataDTO>();

  const {
    partnerQcdYearOptions,
    principalQcdYearOptions,
    donationYearOptions,
    dafStartYearOptions,
    dafEndYearOptions,
    charityNameOptions,
  } = options;

  const donor = watch('donor');
  const givingStrategy = watch('givingStrategy');
  const primaryDob = watch('principalDob');
  const spouseDob = watch('partnerDob');
  const maximizeDonations = watch('maximizeDonations');
  const isRecurring = watch('isRecurring');

  const hasDonor = donor !== null;

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const rawQcdYearOptions =
    hasDonor && donor === Donor.Spouse ? partnerQcdYearOptions : principalQcdYearOptions;

  const rawYearOptions =
    givingStrategy === GivingStrategy.QualifiedCharitableDonations
      ? rawQcdYearOptions
      : donationYearOptions;

  const yearOptions = rawYearOptions.map(year => ({
    label: year.toString(),
    value: year,
    rightLabel:
      primaryYob && hasDonor
        ? donor === Donor.Principal
          ? `Age ${year - primaryYob}`
          : spouseYob
          ? `Age ${year - spouseYob}`
          : ''
        : primaryYob
        ? `Age ${year - primaryYob}${spouseYob ? `, Age ${year - spouseYob}` : ''}`
        : undefined,
  }));

  const startYearOptionsWithAgeLabel = dafStartYearOptions.map(opt => ({
    ...opt,
    rightLabel:
      primaryYob && opt.value
        ? `Age ${opt.value - primaryYob}${spouseYob ? `, Age ${opt.value - spouseYob}` : ''}`
        : undefined,
  }));

  const endYearOptionsWithAgeLabel = dafEndYearOptions.map(opt => ({
    ...opt,
    rightLabel:
      primaryYob && opt.value
        ? `Age ${opt.value - primaryYob}${spouseYob ? `, Age ${opt.value - spouseYob}` : ''}`
        : undefined,
  }));

  return {
    yearOptions,
    isRecurring,
    maximizeDonations,
    startYearOptionsWithAgeLabel,
    endYearOptionsWithAgeLabel,
    givingStrategy,
    charityNameOptions,
  };
};
